import { Context, useContext as useReactContext } from 'react';

import type { Tab, TabsContext } from 'components/Tabs/Tabs';

/**
 * useContext layers a small, cache-oriented data API on top of the raw tab
 * context. Each open tab already acts as a per-id store (its `Data` field), so
 * this hook simply exposes friendlier helpers for reading, writing and — most
 * importantly — lazily loading that data without refetching what is already
 * cached.
 *
 * The hook returns every field of the underlying context (so existing
 * destructures such as `pageContext`, `tabs`, `setOpenTabIndex` keep working)
 * plus the helpers below.
 *
 * Reads:   `data` (selected tab's Data), `getData(id)`, `getTab(id)`
 * Writes:  `update(partial)`, `updateById(id, partial)`, `setTitle(...)`
 * Tabs:    `open(tab)`, `create(tab)`, `openMany(tabs)`, `close(id, urlParamId?)`
 * Cache:   `isLoaded(key)`, `isLoadedById(id, key)`,
 *          `ensure(key, loader)`, `ensureById(id, key, loader)`,
 *          `invalidate(keys)`
 */
export const useContext = <T>(context: Context<TabsContext<T>>) => {

    const ctx = useReactContext(context);
    const { pageContext, tabs, tabsDispatch } = ctx;

    // The currently focused tab's cached data, or null when no tab is open.
    const data = pageContext?.Data ?? null;

    // --- reads / cache lookups -------------------------------------------------

    const getTab = (id: number): Tab<T> | undefined => tabs.find(tab => tab.Id === id);

    const getData = (id: number): T | undefined => getTab(id)?.Data;

    // True when the given tab already has a (non-undefined) value cached for `key`.
    const isLoadedById = (id: number, key: keyof T): boolean => {

        const tab = getTab(id);
        return tab ? tab.Data[key] !== undefined : false;

    };

    const isLoaded = (key: keyof T): boolean => (pageContext ? pageContext.Data[key] !== undefined : false);

    // --- writes ----------------------------------------------------------------

    const updateById = (id: number, partial: Partial<T>) => {

        tabsDispatch({ dataToMergeIn: partial, tabId: id, type: 'UpdateData' });

    };

    // Merge a partial update into the currently focused tab.
    const update = (partial: Partial<T>) => {

        if (pageContext) {

            updateById(pageContext.Id, partial);

        }

    };

    const setTitle = (title: string, hasChanges: boolean, id: number = pageContext?.Id ?? 0) => {

        tabsDispatch({ hasChanges, newTitle: title, tabId: id, type: 'UpdateTitle' });

    };

    // --- open / close ----------------------------------------------------------

    const open = (tab: Tab<T>) => tabsDispatch({ tab, type: 'CreateOrFocus' });

    const create = (tab: Tab<T>) => tabsDispatch({ tab, type: 'CreateNew' });

    // Open a brand new tab when `createNew`, otherwise focus/replace the matching tab.
    const openOrCreate = (tab: Tab<T>, createNew: boolean) => (createNew ? create(tab) : open(tab));

    const openMany = (newTabs: Tab<T>[]) => tabsDispatch({ tabs: newTabs, type: 'FocusMultiple' });

    const close = (id: number, urlParamId?: string) => tabsDispatch({ tabId: id, type: 'Delete', urlParamId });

    // --- caching primitives ----------------------------------------------------

    // Load and cache `key` for the given tab only if it is not already present.
    // This is the core "skip refetch" guard: callers can call it freely on focus
    // without re-issuing network requests for data they already have.
    const ensureById = async <K extends keyof T>(id: number, key: K, loader: () => Promise<T[K]>): Promise<void> => {

        if (isLoadedById(id, key)) {

            return;

        }
        const value = await loader();
        updateById(id, { [key]: value } as unknown as Partial<T>);

    };

    // Same as `ensureById` but targets the currently focused tab.
    const ensure = async <K extends keyof T>(key: K, loader: () => Promise<T[K]>): Promise<void> => {

        if (!pageContext) {

            return;

        }
        await ensureById(pageContext.Id, key, loader);

    };

    // Clear cached values so the next `ensure` for those keys reloads them.
    const invalidate = (keys: (keyof T)[]) => {

        if (!pageContext) {

            return;

        }
        const cleared = {} as Partial<T>;
        keys.forEach(key => {

            cleared[key] = undefined;

        });
        updateById(pageContext.Id, cleared);

    };

    return {
        ...ctx,
        close,
        create,
        data,
        ensure,
        ensureById,
        getData,
        getTab,
        invalidate,
        isLoaded,
        isLoadedById,
        open,
        openMany,
        openOrCreate,
        setTitle,
        update,
        updateById
    };

};
